import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, FileText, DollarSign, CheckCircle, AlertTriangle,
  Download, Building2, Users, Calendar, Shield, ShieldAlert,
  ShieldX, ShieldCheck, TrendingUp, Printer, Filter, Search,
  ChevronDown, ChevronRight, ExternalLink, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSURANCE_STATUS_CONFIG = {
  valid: { label: 'Valid', color: 'bg-green-100 text-green-700', icon: ShieldCheck },
  missing: { label: 'Missing', color: 'bg-red-100 text-red-700', icon: ShieldX },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: ShieldX },
  issues: { label: 'Issues', color: 'bg-amber-100 text-amber-700', icon: ShieldAlert },
  unknown: { label: 'Unknown', color: 'bg-gray-100 text-gray-700', icon: Shield },
};

export default function InsuranceAuditPage() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  
  const [activeTab, setActiveTab] = useState('company');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Report data
  const [companyReport, setCompanyReport] = useState(null);
  const [projectReport, setProjectReport] = useState(null);
  const [expiringReport, setExpiringReport] = useState(null);
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState({});
  
  const darkMode = document.documentElement.classList.contains('dark');
  
  useEffect(() => {
    fetchProjects();
    fetchExpiringInsurance();
  }, []);
  
  useEffect(() => {
    if (activeTab === 'company') {
      fetchCompanyReport();
    } else if (activeTab === 'project' && selectedProjectId) {
      fetchProjectReport();
    }
  }, [activeTab, selectedYear, selectedProjectId]);
  
  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };
  
  const fetchCompanyReport = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/insurance-audit/company`, {
        params: { year: selectedYear }
      });
      setCompanyReport(res.data);
    } catch (error) {
      console.error('Error fetching company report:', error);
      toast.error('Failed to load company report');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProjectReport = async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/insurance-audit/project/${selectedProjectId}`, {
        params: { year: selectedYear }
      });
      setProjectReport(res.data);
    } catch (error) {
      console.error('Error fetching project report:', error);
      toast.error('Failed to load project report');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchExpiringInsurance = async () => {
    try {
      const res = await axios.get(`${API}/reports/contractors-expiring-insurance`, {
        params: { days: 60 }
      });
      setExpiringReport(res.data);
    } catch (error) {
      console.error('Error fetching expiring insurance:', error);
    }
  };
  
  const toggleRowExpanded = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const printReport = () => {
    window.print();
  };
  
  const exportCSV = (data, filename) => {
    if (!data || !data.contractors) return;
    
    const headers = ['Contractor', 'Total Paid', 'Payment Count', 'Insurance Status', 'Issues'];
    const rows = data.contractors.map(c => [
      c.pay_to,
      c.total_paid,
      c.payment_count,
      c.insurance_status,
      (c.issues || c.insurance_issues || []).join('; ')
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${selectedYear}.csv`;
    a.click();
  };
  
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-[#FAF9F7]'}`}>
      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'} sticky top-0 z-10 print:hidden`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/financials')}
                className={darkMode ? 'text-[#8A8A8A] hover:text-[#E5E2DD]' : ''}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Financials
              </Button>
              <div className="flex items-center gap-2">
                <HelpIcon elementId="insurance-audit" position="inline" />
                <div>
                  <h1 className="text-xl font-semibold flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Insurance Audit Reports
                  </h1>
                  <p className="text-sm text-muted-foreground">Contractor payment tracking for insurance audits</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={printReport}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => exportCSV(activeTab === 'company' ? companyReport : projectReport, activeTab === 'company' ? 'company-audit' : 'project-audit')}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Expiring Insurance Alert */}
        {expiringReport && (expiringReport.summary?.expired_count > 0 || expiringReport.summary?.expiring_count > 0) && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 border-amber-500 ${darkMode ? 'bg-amber-900/20' : 'bg-amber-50'}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className={`font-medium ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                  Insurance Attention Needed
                </p>
                <p className={`text-sm ${darkMode ? 'text-amber-300/70' : 'text-amber-700'}`}>
                  {expiringReport.summary.expired_count > 0 && `${expiringReport.summary.expired_count} contractor(s) with expired insurance. `}
                  {expiringReport.summary.expiring_count > 0 && `${expiringReport.summary.expiring_count} contractor(s) with insurance expiring within 60 days.`}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Filters */}
        <div className={`p-4 rounded-lg border mb-6 ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
          <div className="flex items-center gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Report Year</Label>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {activeTab === 'project' && (
              <div>
                <Label className="text-xs text-muted-foreground">Project</Label>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="company" className="gap-2">
              <Building2 className="w-4 h-4" />
              Company-Wide
            </TabsTrigger>
            <TabsTrigger value="project" className="gap-2">
              <FileText className="w-4 h-4" />
              By Project
            </TabsTrigger>
            <TabsTrigger value="expiring" className="gap-2">
              <Clock className="w-4 h-4" />
              Expiring Insurance
              {expiringReport?.summary?.expired_count > 0 && (
                <Badge variant="destructive" className="ml-1">{expiringReport.summary.expired_count}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Company-Wide Tab */}
          <TabsContent value="company">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BC5D41]"></div>
              </div>
            ) : companyReport ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <Users className="w-4 h-4" />
                      Total Contractors
                    </div>
                    <p className="text-2xl font-semibold">{companyReport.summary?.total_contractors || 0}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <DollarSign className="w-4 h-4" />
                      Total Paid
                    </div>
                    <p className="text-2xl font-semibold">{formatCurrency(companyReport.summary?.total_paid)}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <ShieldCheck className="w-4 h-4 text-green-600" />
                      Valid Insurance
                    </div>
                    <p className="text-2xl font-semibold text-green-600">{companyReport.summary?.contractors_with_valid_insurance || 0}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <ShieldX className="w-4 h-4 text-red-600" />
                      Missing/Expired
                    </div>
                    <p className="text-2xl font-semibold text-red-600">
                      {(companyReport.summary?.contractors_missing_insurance || 0) + (companyReport.summary?.contractors_expired_insurance || 0)}
                    </p>
                  </div>
                </div>
                
                {/* Contractors Table */}
                <div className={`rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                  <div className="p-4 border-b border-inherit">
                    <h2 className="font-medium">{selectedYear} Contractor Payments</h2>
                    <p className="text-sm text-muted-foreground">All contractors who received payment this year</p>
                  </div>
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30px]"></TableHead>
                        <TableHead>Contractor</TableHead>
                        <TableHead className="text-right">Total Paid</TableHead>
                        <TableHead className="text-center">Payments</TableHead>
                        <TableHead className="text-center">Projects</TableHead>
                        <TableHead>Insurance Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companyReport.contractors?.map((contractor) => {
                        const StatusIcon = INSURANCE_STATUS_CONFIG[contractor.insurance_status]?.icon || Shield;
                        const isExpanded = expandedRows[contractor.contractor_id || contractor.pay_to];
                        
                        return (
                          <>
                            <TableRow key={contractor.contractor_id || contractor.pay_to}>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0"
                                  onClick={() => toggleRowExpanded(contractor.contractor_id || contractor.pay_to)}
                                >
                                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </Button>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{contractor.pay_to}</p>
                                  {contractor.contact_name && (
                                    <p className="text-xs text-muted-foreground">{contractor.contact_name}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-semibold">{formatCurrency(contractor.total_paid)}</TableCell>
                              <TableCell className="text-center">{contractor.payment_count}</TableCell>
                              <TableCell className="text-center">{contractor.projects?.length || 0}</TableCell>
                              <TableCell>
                                <Badge className={INSURANCE_STATUS_CONFIG[contractor.insurance_status]?.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {INSURANCE_STATUS_CONFIG[contractor.insurance_status]?.label}
                                </Badge>
                                {contractor.issues?.length > 0 && (
                                  <p className="text-xs text-red-600 mt-1">{contractor.issues[0]}</p>
                                )}
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow className={darkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}>
                                <TableCell colSpan={6} className="p-4">
                                  <div className="space-y-3">
                                    <div>
                                      <p className="text-sm font-medium mb-2">Projects</p>
                                      <div className="flex flex-wrap gap-2">
                                        {contractor.projects?.map((proj, idx) => (
                                          <Badge key={idx} variant="outline">
                                            {proj.project_name}: {formatCurrency(proj.amount)}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                    {contractor.insurance_documents?.length > 0 && (
                                      <div>
                                        <p className="text-sm font-medium mb-2">Insurance Documents</p>
                                        <div className="space-y-1">
                                          {contractor.insurance_documents.map((doc, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-sm">
                                              <Shield className="w-3 h-3" />
                                              <span className="capitalize">{doc.type?.replace('_', ' ')}</span>
                                              {doc.expiration_date && (
                                                <span className="text-muted-foreground">
                                                  Expires: {doc.expiration_date}
                                                </span>
                                              )}
                                              {doc.file_url && (
                                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-[#BC5D41] hover:underline">
                                                  <ExternalLink className="w-3 h-3" />
                                                </a>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No data available for {selectedYear}</p>
              </div>
            )}
          </TabsContent>
          
          {/* Project Tab */}
          <TabsContent value="project">
            {!selectedProjectId ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a project to view its insurance audit report</p>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BC5D41]"></div>
              </div>
            ) : projectReport ? (
              <div className="space-y-6">
                {/* Project Summary */}
                <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                  <h2 className="font-medium text-lg">{projectReport.project_name}</h2>
                  <p className="text-sm text-muted-foreground">Insurance audit for {selectedYear}</p>
                </div>
                
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <Users className="w-4 h-4" />
                      Contractors
                    </div>
                    <p className="text-2xl font-semibold">{projectReport.summary?.total_contractors || 0}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <DollarSign className="w-4 h-4" />
                      Total Paid
                    </div>
                    <p className="text-2xl font-semibold">{formatCurrency(projectReport.summary?.total_paid)}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <ShieldAlert className="w-4 h-4 text-amber-600" />
                      Insurance Issues
                    </div>
                    <p className="text-2xl font-semibold text-amber-600">{projectReport.summary?.contractors_with_insurance_issues || 0}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <ShieldX className="w-4 h-4 text-red-600" />
                      Missing Insurance
                    </div>
                    <p className="text-2xl font-semibold text-red-600">{projectReport.summary?.contractors_missing_insurance || 0}</p>
                  </div>
                </div>
                
                {/* Contractors Table */}
                <div className={`rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30px]"></TableHead>
                        <TableHead>Contractor</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead className="text-right">Total Paid</TableHead>
                        <TableHead>Insurance Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectReport.contractors?.map((contractor) => {
                        const StatusIcon = INSURANCE_STATUS_CONFIG[contractor.insurance_status]?.icon || Shield;
                        const isExpanded = expandedRows[contractor.contractor_id || contractor.pay_to];
                        
                        return (
                          <>
                            <TableRow key={contractor.contractor_id || contractor.pay_to}>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0"
                                  onClick={() => toggleRowExpanded(contractor.contractor_id || contractor.pay_to)}
                                >
                                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </Button>
                              </TableCell>
                              <TableCell className="font-medium">{contractor.pay_to}</TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  {contractor.contact_name && <p>{contractor.contact_name}</p>}
                                  {contractor.phone && <p className="text-muted-foreground">{contractor.phone}</p>}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-semibold">{formatCurrency(contractor.total_paid)}</TableCell>
                              <TableCell>
                                <Badge className={INSURANCE_STATUS_CONFIG[contractor.insurance_status]?.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {INSURANCE_STATUS_CONFIG[contractor.insurance_status]?.label}
                                </Badge>
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow className={darkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}>
                                <TableCell colSpan={5} className="p-4">
                                  <div className="space-y-3">
                                    <p className="text-sm font-medium">Payment History</p>
                                    <div className="space-y-1">
                                      {contractor.payments?.map((payment, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-sm">
                                          <div className="flex items-center gap-2">
                                            <Badge variant="outline">Draw #{payment.draw_number}</Badge>
                                            <span className="text-muted-foreground">{payment.date}</span>
                                            <span>{payment.description}</span>
                                          </div>
                                          <span className="font-medium">{formatCurrency(payment.amount)}</span>
                                        </div>
                                      ))}
                                    </div>
                                    {contractor.insurance_issues?.length > 0 && (
                                      <div className="mt-2 p-2 rounded bg-red-50 text-red-700 text-sm">
                                        <p className="font-medium">Insurance Issues:</p>
                                        <ul className="list-disc list-inside">
                                          {contractor.insurance_issues.map((issue, idx) => (
                                            <li key={idx}>{issue}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No payment data for this project in {selectedYear}</p>
              </div>
            )}
          </TabsContent>
          
          {/* Expiring Insurance Tab */}
          <TabsContent value="expiring">
            {expiringReport ? (
              <div className="space-y-6">
                {/* Expired */}
                {expiringReport.expired?.length > 0 && (
                  <div className={`rounded-lg border border-red-200 ${darkMode ? 'bg-red-900/10' : 'bg-red-50'}`}>
                    <div className="p-4 border-b border-red-200">
                      <h2 className="font-medium text-red-700 flex items-center gap-2">
                        <ShieldX className="w-5 h-5" />
                        Expired Insurance ({expiringReport.expired.length})
                      </h2>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contractor</TableHead>
                          <TableHead>Document Type</TableHead>
                          <TableHead>Expired On</TableHead>
                          <TableHead>Days Expired</TableHead>
                          <TableHead>Contact</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expiringReport.expired.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.company_name}</TableCell>
                            <TableCell className="capitalize">{item.doc_type?.replace('_', ' ')}</TableCell>
                            <TableCell>{item.expiration_date}</TableCell>
                            <TableCell>
                              <Badge variant="destructive">{item.days_expired} days</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{item.contact_email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                {/* Expiring Soon */}
                {expiringReport.expiring_soon?.length > 0 && (
                  <div className={`rounded-lg border border-amber-200 ${darkMode ? 'bg-amber-900/10' : 'bg-amber-50'}`}>
                    <div className="p-4 border-b border-amber-200">
                      <h2 className="font-medium text-amber-700 flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5" />
                        Expiring Soon ({expiringReport.expiring_soon.length})
                      </h2>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contractor</TableHead>
                          <TableHead>Document Type</TableHead>
                          <TableHead>Expires On</TableHead>
                          <TableHead>Days Remaining</TableHead>
                          <TableHead>Contact</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expiringReport.expiring_soon.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.company_name}</TableCell>
                            <TableCell className="capitalize">{item.doc_type?.replace('_', ' ')}</TableCell>
                            <TableCell>{item.expiration_date}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-amber-100 text-amber-700">
                                {item.days_until_expiration} days
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{item.contact_email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                {expiringReport.expired?.length === 0 && expiringReport.expiring_soon?.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p className="text-green-600 font-medium">All contractor insurance is current!</p>
                    <p className="text-sm">No expired or expiring insurance documents found.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BC5D41]"></div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
